<script setup lang="ts">
/**
 * HelpHint — a small "?" affordance that reveals a short explanation.
 *
 * Used to teach the interface in place (what an assistant is, why sources
 * are clickable, what a tool round-trip means) without a separate docs trip.
 * Opens on hover AND on click/focus so it works for mouse, keyboard, and
 * touch; closes on Escape, outside click, or blur. The popover is plain
 * text via the `text` prop, or richer content via the default slot.
 *
 * `wide` + `align="start"` is the page-description variant: a paragraph
 * beside a tab heading rather than a one-liner beside a field. Those may
 * contain links, so focus moving INTO the popover must not close it, and
 * the pointer must be able to cross the gap between trigger and popover.
 */
import { onBeforeUnmount, onMounted, ref } from "vue";

defineProps<{
  /** Plain-text explanation. Use the default slot instead for rich content. */
  text?: string;
  /** Accessible label for the trigger; defaults to "Help". */
  label?: string;
  /** Paragraph-width popover, for page descriptions. */
  wide?: boolean;
  /** `start` anchors the popover's left edge to the trigger — use beside a
   *  heading at the page's left edge, where centring would clip it. */
  align?: "center" | "start";
}>();

const open = ref(false);
const root = ref<HTMLElement | null>(null);

function show() {
  open.value = true;
}
function hide() {
  open.value = false;
}
function toggle() {
  open.value = !open.value;
}
function onFocusOut(e: FocusEvent) {
  // Focus moving to a link inside the popover is not "leaving".
  if (root.value?.contains(e.relatedTarget as Node | null)) return;
  hide();
}
function onKey(e: KeyboardEvent) {
  if (e.key === "Escape") hide();
}
function onDocClick(e: MouseEvent) {
  if (!open.value) return;
  if (root.value && !root.value.contains(e.target as Node)) hide();
}
onMounted(() => document.addEventListener("click", onDocClick));
onBeforeUnmount(() => document.removeEventListener("click", onDocClick));
</script>

<template>
  <span
    ref="root"
    class="help-hint"
    :class="{ 'help-hint--wide': wide, 'help-hint--start': align === 'start' }"
    @mouseenter="show"
    @mouseleave="hide"
    @focusout="onFocusOut"
    @keydown="onKey"
  >
    <button
      type="button"
      class="help-hint__trigger"
      :aria-label="label ?? 'Help'"
      :aria-expanded="open"
      @click.stop="toggle"
      @focus="show"
    >
      ?
    </button>
    <Transition name="hint-fade">
      <span v-if="open" class="help-hint__pop glass-strong" role="tooltip">
        <slot>{{ text }}</slot>
      </span>
    </Transition>
  </span>
</template>

<style scoped>
.help-hint {
  position: relative;
  display: inline-flex;
  vertical-align: middle;
}
.help-hint__trigger {
  display: grid;
  place-items: center;
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 50%;
  border: 1px solid var(--color-border);
  background: var(--glass-bg);
  color: var(--color-muted);
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  cursor: help;
  transition:
    color var(--dur-1) var(--ease-out),
    border-color var(--dur-1) var(--ease-out);
}
.help-hint__trigger:hover,
.help-hint__trigger[aria-expanded="true"] {
  color: var(--color-accent);
  border-color: color-mix(in srgb, var(--color-accent) 45%, transparent);
}
.help-hint__pop {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 50%;
  transform: translateX(-50%);
  z-index: 80;
  width: max-content;
  max-width: 17rem;
  padding: 0.6rem 0.75rem;
  border-radius: var(--radius-md);
  font-size: 0.82rem;
  font-weight: 400;
  line-height: 1.5;
  color: var(--color-ink);
  text-align: left;
  letter-spacing: normal;
  text-transform: none;
}
/* Bridges the gap to the trigger so the pointer can reach links inside. */
.help-hint__pop::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: -0.5rem;
  height: 0.5rem;
}
/* Solid, not frosted: a paragraph has to stay readable over a dense table,
   which 88% glass does not manage wherever backdrop-filter is unavailable. */
.help-hint--wide .help-hint__pop {
  width: 26rem;
  max-width: min(26rem, 80vw);
  background: var(--color-surface);
}
.help-hint--start .help-hint__pop {
  left: -0.5rem;
  transform: none;
}
.help-hint--start .hint-fade-enter-from,
.help-hint--start .hint-fade-leave-to {
  transform: translateY(-4px);
}
/* Nudge the popover left when it would overflow the right edge on narrow
   viewports. */
@media (max-width: 480px) {
  .help-hint__pop {
    left: auto;
    right: 0;
    transform: none;
  }
}
.hint-fade-enter-active,
.hint-fade-leave-active {
  transition:
    opacity var(--dur-1) var(--ease-out),
    transform var(--dur-1) var(--ease-out);
}
.hint-fade-enter-from,
.hint-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-4px);
}
</style>
